import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router';

import { apiGet, apiPost, setCsrfToken } from './client';
import type { AuthState } from './types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  login: (password: string, email?: string) => Promise<void>;
  acceptInvitation: (token: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Гасит сессию локально — вызывается перехватчиком 401 из QueryProvider. */
  expire: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyState(state: AuthState): AuthStatus {
  setCsrfToken(state.csrf_token ?? null);
  return state.authenticated ? 'authenticated' : 'anonymous';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    apiGet<AuthState>('/api/auth/me')
      .then((state) => {
        if (!cancelled) setStatus(applyState(state));
      })
      .catch(() => {
        // 401 на /api/auth/me — нормальный ответ «сессии нет», не ошибка
        if (!cancelled) {
          setCsrfToken(null);
          setStatus('anonymous');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (password: string, email?: string) => {
    await apiPost<AuthState>('/api/auth/login', { password, ...(email ? { email } : {}) });
    // Источник истины по CSRF — только /api/auth/me (Д-Ф3)
    const state = await apiGet<AuthState>('/api/auth/me');
    setStatus(applyState(state));
  }, []);

  const acceptInvitation = useCallback(async (token: string, password: string, displayName?: string) => {
    await apiPost<AuthState>('/api/auth/invitations/accept', {
      token,
      password,
      ...(displayName ? { display_name: displayName } : {}),
    });
    const state = await apiGet<AuthState>('/api/auth/me');
    setStatus(applyState(state));
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost<Record<string, never>>('/api/auth/logout');
    } finally {
      setCsrfToken(null);
      setStatus('anonymous');
    }
  }, []);

  const expire = useCallback(() => {
    setCsrfToken(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, login, acceptInvitation, logout, expire }),
    [status, login, acceptInvitation, logout, expire],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth вызван вне AuthProvider');
  return value;
}

/**
 * Д-Ф4: при отсутствии сессии уводим на вход и запоминаем исходный маршрут.
 * Владелец открывает карточку поездки по ссылке из Telegram — после входа
 * он обязан попасть именно туда, а не на главную.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div role="status" className="p-8 text-ink-dim">
        проверяем сессию…
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
